import React, { useState, useEffect } from 'react';
import { Phone, Wifi, Camera, Sparkles, Armchair, Refrigerator, MapPin, Calendar, User, Mail, Clock, ArrowUp, Droplet } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import lounge from './assets/lounge.jpg';
import parking from './assets/parking.jpg';
import exterior from './assets/exterior.jpg';
import lift from './assets/lift.jpg';
import washing from './assets/washing.jpg';
import skyline from './assets/skyline.jpg';
import rooms from './assets/rooms.jpg';

// Firebase Configuration - REPLACE WITH YOUR CONFIG
const firebaseConfig = {
  apiKey: "AIza...",
  authDomain: "shanmukha-pg.firebaseapp.com",
  projectId: "shanmukha-pg",
  storageBucket: "shanmukha-pg.appspot.com",
  messagingSenderId: "123456789",
  appId: "1:123456789:web:abc123"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export default function ShanmukhaPGWebsite() {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    visitDate: '',
    visitTime: '',
    roomType: '1-sharing',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.visitDate || !formData.visitTime) {
      alert('Please fill all required fields');
      return;
    }

    try {
      // Save to Firebase Firestore
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        visitDate: formData.visitDate,
        visitTime: formData.visitTime,
        roomType: formData.roomType,
        message: formData.message,
        status: 'pending',
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'bookings'), bookingData);
      console.log('Booking saved with ID:', docRef.id);

      // Update local state
      const newBooking = { ...formData, id: docRef.id, bookingDate: new Date().toISOString() };
      setBookings([...bookings, newBooking]);
      setSubmitted(true);
      
      setTimeout(() => {
        setShowBookingModal(false);
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          visitDate: '',
          visitTime: '',
          roomType: '1-sharing',
          message: ''
        });
      }, 3000);

    } catch (error) {
      console.error('Error saving booking:', error);
      alert('Error saving booking. Please try calling us directly at 9767393393');
    }
  };

  const amenities = [
    { icon: <Wifi size={40} />, title: 'Free Wi-Fi', desc: 'High-speed internet throughout' },
    { icon: <Camera size={40} />, title: '24/7 CCTV', desc: 'Complete security surveillance' },
    { icon: <ArrowUp size={40} />, title: 'Lift Available', desc: 'Easy access to all floors' },
    { icon: <Sparkles size={40} />, title: 'Daily Cleaning', desc: 'Housekeeping services daily' },
    { icon: <Armchair size={40} />, title: 'Fully Furnished', desc: 'Move-in ready with essentials' },
    { icon: <Refrigerator size={40} />, title: 'Fridge', desc: 'Refrigerator facility available' },
    { icon: <Droplet size={40} />, title: 'Washing Machine', desc: 'Laundry facilities on rooftop' },
    { icon: <span className="text-4xl">🔥</span>, title: 'Geyser', desc: 'Hot water facility available' }
  ];

  const galleryImages = [
  { id: 1, src: lounge, label: 'Spacious Lounge Area', desc: 'Comfortable seating with modern ambiance' },
  { id: 2, src: parking, label: 'Parking & Reception', desc: 'Secure bike parking with reception area' },
  { id: 3, src: exterior, label: 'Building Exterior', desc: 'Modern building with security gate' },
  { id: 4, src: lift, label: 'Modern Lift', desc: 'Easy access with marble interiors' },
  { id: 5, src: washing, label: 'Rooftop Washing Area', desc: 'Washing machines and drying space' },
  { id: 6, src: skyline, label: 'Pune Skyline View', desc: 'Beautiful city views from rooftop' },
  { id: 7, src: rooms, label: 'Comfortable Rooms', desc: 'Well-furnished sharing rooms' }
];
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-gradient-to-r from-gray-900 to-gray-800 text-yellow-500 shadow-lg z-50">
        <nav className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 text-xl md:text-2xl font-bold">
              <span className="text-2xl md:text-3xl">🕉️</span>
              <span>OM SAI RAM PG</span>
            </div>
            <ul className="hidden md:flex gap-8">
              <li><a href="#home" className="hover:text-white transition">Home</a></li>
              <li><a href="#gallery" className="hover:text-white transition">Gallery</a></li>
              <li><a href="#amenities" className="hover:text-white transition">Amenities</a></li>
              <li><a href="#rooms" className="hover:text-white transition">Rooms</a></li>
              <li><a href="#contact" className="hover:text-white transition">Contact</a></li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-yellow-500 mb-4">Welcome to OM SAI RAM PG</h1>
          <p className="text-xl md:text-2xl mb-4 text-gray-300">Affordable Living, Premium Comfort</p>
          <p className="text-lg md:text-xl mb-8 text-yellow-400">Saikrupa Society, Somnath Nagar, Wadgaon Sheri</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button 
              onClick={() => setShowBookingModal(true)}
              className="bg-yellow-500 text-gray-900 px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold text-base md:text-lg hover:bg-yellow-400 transform hover:scale-105 transition flex items-center gap-2"
            >
              <Calendar size={24} />
              Book Your Visit
            </button>
            <a 
              href="tel:9767393393"
              className="bg-transparent border-2 border-yellow-500 text-yellow-500 px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold text-base md:text-lg hover:bg-yellow-500 hover:text-gray-900 transition flex items-center gap-2"
            >
              <Phone size={24} />
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            Photo Gallery
            <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4"></div>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((item) => (
              <div 
                key={item.id} 
                className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition group cursor-pointer"
                onClick={() => setSelectedImage(item)}
              >
                <img 
                  src={item.src}
                  alt={item.label}
                  className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                  <h3 className="text-white text-lg font-bold">{item.label}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section id="amenities" className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            Premium Amenities
            <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4"></div>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {amenities.map((amenity, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition text-center border border-gray-100">
                <div className="text-yellow-500 flex justify-center mb-4">{amenity.icon}</div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{amenity.title}</h3>
                <p className="text-gray-600 text-sm">{amenity.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Room Types */}
      <section id="rooms" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            Room Options
            <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4"></div>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {['Single Sharing', 'Double Sharing', 'Triple Sharing'].map((room, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition">
                <div className="text-5xl mb-4">
                  {idx === 0 ? '🛏️' : idx === 1 ? '🛏️🛏️' : '🛏️🛏️🛏️'}
                </div>
                <h3 className="text-2xl font-bold text-yellow-500 mb-2">{room}</h3>
                <p className="text-gray-300">Perfect for {idx === 0 ? 'privacy seekers' : idx === 1 ? 'close friends' : 'budget conscious'}</p>
                <button 
                  onClick={() => setShowBookingModal(true)}
                  className="mt-6 w-full bg-yellow-500 text-gray-900 py-3 rounded-lg font-bold hover:bg-yellow-400 transition"
                >
                  Book Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section id="location" className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-yellow-500">
            Prime Location
            <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4"></div>
          </h2>
          <div className="flex items-center justify-center gap-2 text-xl md:text-2xl mb-4">
            <MapPin size={32} className="text-yellow-500" />
            <span>Saikrupa Society, Somnath Nagar</span>
          </div>
          <p className="text-lg md:text-xl mb-2">Wadgaon Sheri, Pune - 411014</p>
          <p className="text-gray-400 text-base md:text-lg">Easy access to IT parks, shopping centers, and public transport</p>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-600">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            Get in Touch
            <div className="w-24 h-1 bg-gray-900 mx-auto mt-4"></div>
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-gray-900">Call us now or book your visit online!</p>
          <div className="flex justify-center gap-4 md:gap-8 flex-wrap mb-8">
            <a href="tel:9767393393" className="flex items-center gap-2 text-xl md:text-2xl font-bold text-gray-900 hover:scale-105 transition">
              <Phone size={28} />
              9767393393
            </a>
            <a href="tel:8830146848" className="flex items-center gap-2 text-xl md:text-2xl font-bold text-gray-900 hover:scale-105 transition">
              <Phone size={28} />
              8830146848
            </a>
          </div>
          <button 
            onClick={() => setShowBookingModal(true)}
            className="bg-gray-900 text-yellow-500 px-8 md:px-10 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-gray-800 transform hover:scale-105 transition"
          >
            Book Your Visit Online
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-yellow-500 py-8 text-center">
        <p className="text-lg">&copy; 2025 OM SAI RAM PG. All rights reserved.</p>
        <p className="text-gray-400 mt-2">Affordable Living, Premium Comfort</p>
        {bookings.length > 0 && (
          <p className="text-sm text-gray-600 mt-4">Total Bookings: {bookings.length}</p>
        )}
      </footer>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-6 rounded-t-2xl sticky top-0">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Book Your Visit</h3>
                <button 
                  onClick={() => {
                    setShowBookingModal(false);
                    setSubmitted(false);
                  }}
                  className="text-gray-900 hover:text-white text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            </div>
            
            {!submitted ? (
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      <User size={20} className="inline mr-2" />
                      Full Name *
                    </label>
                    <input 
                      type="text"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      <Phone size={20} className="inline mr-2" />
                      Phone Number *
                    </label>
                    <input 
                      type="tel"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      <Mail size={20} className="inline mr-2" />
                      Email
                    </label>
                    <input 
                      type="email"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter email (optional)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      <Calendar size={20} className="inline mr-2" />
                      Visit Date *
                    </label>
                    <input 
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                      value={formData.visitDate}
                      onChange={(e) => handleInputChange('visitDate', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      <Clock size={20} className="inline mr-2" />
                      Preferred Time *
                    </label>
                    <input 
                      type="time"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                      value={formData.visitTime}
                      onChange={(e) => handleInputChange('visitTime', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Room Type *
                    </label>
                    <select 
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                      value={formData.roomType}
                      onChange={(e) => handleInputChange('roomType', e.target.value)}
                    >
                      <option value="1-sharing">Single Sharing</option>
                      <option value="2-sharing">Double Sharing</option>
                      <option value="3-sharing">Triple Sharing</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-6">
                  <label className="block text-gray-700 font-semibold mb-2">
                    Message (Optional)
                  </label>
                  <textarea 
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Any special requirements or questions?"
                  />
                </div>
                
                <button 
                  onClick={handleSubmit}
                  className="w-full mt-6 bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 py-4 rounded-lg font-bold text-xl hover:from-yellow-400 hover:to-yellow-500 transition"
                >
                  Confirm Booking
                </button>
              </div>
            ) : (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4">✅</div>
                <h4 className="text-3xl font-bold text-gray-900 mb-4">Booking Confirmed!</h4>
                <p className="text-xl text-gray-600 mb-2">Thank you, {formData.name}!</p>
                <p className="text-gray-600">We'll contact you at {formData.phone}</p>
                <p className="text-gray-600">Visit scheduled: {formData.visitDate} at {formData.visitTime}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={() => setSelectedImage(null)}>
          <div className="max-w-4xl w-full text-center">
            <img src={selectedImage.src} alt={selectedImage.label} className="w-full rounded-xl mb-4 max-h-[70vh] object-contain" />
            <h3 className="text-3xl font-bold text-yellow-500 mb-4">{selectedImage.label}</h3>
            <p className="text-xl text-gray-300 mb-4">{selectedImage.desc}</p>
            <button 
              onClick={() => setSelectedImage(null)}
              className="bg-yellow-500 text-gray-900 px-8 py-3 rounded-lg font-bold hover:bg-yellow-400 transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}